import type { Session } from "@supabase/supabase-js";

import { isErrorEnvelope } from "@tinkr/shared/error-envelope";
import { currentApiSession, setCurrentApiSession, apiSessionSignal } from "../api-session";
import { recordRequest } from "../performance";
import { invalidateDashboardResources } from "../dashboard-resource";

// MERGED-CONTRACT-005: when the response is a well-formed Tinkr API
// error envelope, fields come straight from the shared shape. We keep
// `ApiErrorEnvelope` (all fields optional) for the looser fallback —
// edge layers (Cloudflare 502, BFF gateway errors) sometimes return
// JSON that *looks* like an envelope but is missing fields like
// requestId, and the client must tolerate that without crashing.
type ApiErrorEnvelope = {
  error?: {
    code?: string;
    message?: string;
    requestId?: string;
    details?: unknown;
  };
};

export class TinkrApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;
  readonly retryAfterMs: number;

  constructor(input: {
    status: number;
    code?: string;
    message?: string;
    requestId?: string | null;
    details?: unknown;
    retryAfterMs?: number;
  }) {
    super(input.message ?? "Tinkr API request failed");
    this.name = "TinkrApiError";
    this.status = input.status;
    this.code = input.code ?? "api_request_failed";
    this.requestId = input.requestId ?? null;
    this.details = input.details ?? null;
    this.retryAfterMs = input.retryAfterMs ?? 0;
  }
}

export type ReauthPurpose =
  | "payout_change"
  | "admin_action"
  | "api_key_reveal"
  | "impersonation"
  | "generic"
  | "account_deletion"
  | "data_export"
  | "finance_export";

type ReauthHandler = (input: {
  error: TinkrApiError;
  purpose: ReauthPurpose;
  session: Session;
}) => Promise<Session | null | void>;

let reauthHandler: ReauthHandler | null = null;

export function setTinkrApiReauthHandler(handler: ReauthHandler | null): void {
  reauthHandler = handler;
}

export function idempotencyKey(prefix: string): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function reauthPurposeFromDetails(details: unknown): ReauthPurpose | null {
  if (typeof details !== "object" || details === null) return null;
  const purpose = (details as { purpose?: unknown }).purpose;
  return purpose === "payout_change" ||
    purpose === "admin_action" ||
    purpose === "api_key_reveal" ||
    purpose === "impersonation" ||
    purpose === "generic" ||
    purpose === "account_deletion" ||
    purpose === "data_export" ||
    purpose === "finance_export"
    ? purpose
    : null;
}

// Module-level memo of the most recent failed request's id. Triage color
// for the feedback dialog: when a user files a bug right after something
// errored, attaching this id lets support find the exact server log line.
// Never used for control flow.
let lastErrorRequestId: string | null = null;

export function getLastErrorRequestId(): string | null {
  return lastErrorRequestId;
}

async function readResponse<T>(response: Response): Promise<T> {
  // No representation exists on 204, even if a JSON content-type remains.
  if (response.status === 204) return null as T;
  const contentType = response.headers.get("content-type") ?? "";
  let body: unknown = null;
  if (contentType.includes("application/json")) {
    try {
      body = await response.json();
    } catch (error) {
      // Preserve aborts and transport errors. Invalid JSON from a gateway is
      // not a successful write, and must not trigger an automatic replay.
      if (!(error instanceof SyntaxError)) throw error;
      if (response.ok) {
        throw new TinkrApiError({ status: 502, code: "invalid_response" });
      }
    }
  }

  if (!response.ok) {
    // Prefer the shared envelope shape when the response matches it
    // (well-formed Tinkr API error). Fall back to the loose
    // ApiErrorEnvelope when the response is a partial / non-Tinkr
    // gateway error.
    if (isErrorEnvelope(body)) {
      lastErrorRequestId = body.error.requestId ?? lastErrorRequestId;
      throw new TinkrApiError({
        retryAfterMs: retryAfterMilliseconds(response.headers.get("retry-after")),
        status: response.status,
        code: body.error.code,
        message: body.error.message,
        requestId: body.error.requestId,
        details: body.error.details
      });
    }
    const envelope = body as ApiErrorEnvelope | null;
    lastErrorRequestId = envelope?.error?.requestId ?? lastErrorRequestId;
    throw new TinkrApiError({
      retryAfterMs: retryAfterMilliseconds(response.headers.get("retry-after")),
      status: response.status,
      code: envelope?.error?.code,
      message: envelope?.error?.message,
      requestId: envelope?.error?.requestId ?? null,
      details: envelope?.error?.details
    });
  }

  if (body === null || typeof body !== "object") {
    throw new TinkrApiError({ status: 502, code: "invalid_response" });
  }
  return body as T;
}

export function retryAfterMilliseconds(value: string | null, now = Date.now()): number {
  if (!value) return 0;
  const seconds = Number(value);
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - now;
  return Number.isFinite(delay) ? Math.max(0, Math.min(300_000, delay)) : 0;
}

/** One bounded request; mutations are NEVER automatically replayed. Polling
 * resources own retry/backoff, avoiding stacked retry layers and failed-call noise. */
async function requestJson<T>(path: string, init: RequestInit, sessionSignal?: AbortSignal): Promise<T> {
  const controller = new AbortController();
  const signals = [init.signal, sessionSignal].filter((s): s is AbortSignal => Boolean(s));
  const abort = () => controller.abort(signals.find(s => s.aborted)?.reason);
  if (signals.some(s => s.aborted)) abort();
  signals.forEach(s => s.addEventListener("abort", abort, { once: true }));
  const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), 35_000);
  const started = performance.now();
  let outcome: "ok" | "failed" | "aborted" = "ok";
  try {
    return await readResponse<T>(await fetch("/api" + path, { ...init, cache: "no-store", signal: controller.signal }));
  } catch (error) {
    outcome = controller.signal.aborted && controller.signal.reason?.name !== "TimeoutError" ? "aborted" : "failed";
    throw error;
  } finally {
    recordRequest(performance.now() - started, outcome);
    clearTimeout(timer);
    signals.forEach(s => s.removeEventListener("abort", abort));
  }
}

function invalidateAfterWrite(path: string): void {
  const notification = path.match(/^\/v1\/(admin|provider|partner)\/notifications(?:\/|$)/);
  const announcement = path.match(/^\/v1\/(admin|provider|partner)\/announcements(?:\/|$)/);
  let prefixes: string[];
  if (notification) {
    prefixes = [notification[1] + "-notification"];
  } else if (announcement) {
    prefixes = [announcement[1] + "-announcements:"];
  } else if (/^\/v1\/provider\/(offers|jobs|quality-incidents)(\/|$)/.test(path)) {
    prefixes = ["provider-offers:", "provider-work-quality:", "provider-fulfillment-standing:", "warm-p1-payables:", "provider-settlements:"];
  } else if (/^\/v1\/provider\/(profile|availability|shipping-eligibility)(\/|$)/.test(path)) {
    prefixes = ["provider-profile:", "provider-operations:", "provider-pricing-guidance:", "warm-shop-status:provider:"];
  } else if (/^\/v1\/provider\/capabilities(\/|$)/.test(path)) {
    prefixes = ["provider-capabilities:", "provider-profile:", "provider-operations:", "provider-quote-simulator:", "provider-pricing-guidance:"];
  } else if (/^\/v1\/provider\/rate-cards(\/|$)/.test(path)) {
    prefixes = ["provider-rate-cards:", "provider-operations:", "provider-quote-simulator:", "provider-pricing-guidance:"];
  } else if (/^\/v1\/provider\/pricing-guidance(\/|$)/.test(path)) {
    prefixes = ["provider-rate-cards:", "provider-operations:", "provider-quote-simulator:", "provider-pricing-guidance:"];
  } else if (/^\/v1\/provider\/(colors|specialties)(\/|$)/.test(path)) {
    prefixes = ["provider-" + path.split("/")[3] + ":"];
  } else if (/^\/v1\/provider\/shipping-(rates|setup)(\/|$)/.test(path)) {
    prefixes = ["provider-shipping-rates:", "provider-profile:", "provider-operations:", "provider-quote-simulator:", "provider-pricing-guidance:", "warm-shop-status:provider:"];
  } else if (path.startsWith("/v1/provider/")) {
    // Unclassified setup writes keep the conservative fallback. Hot work
    // mutations above never reload catalogs, rate cards, colors or settings.
    prefixes = ["provider-", "warm-shop-status:provider:", "warm-p1-", "warm-p4-"];
  } else if (path.startsWith("/v1/partner/")) {
    prefixes = ["partner-", "warm-shop-status:partner:"];
  } else if (path.startsWith('/v1/admin/finance/')) {
    // Reporting decisions/import requests do not mutate provider, payout or
    // dashboard operational state. Finance owns its targeted refresh/polling.
    prefixes = ['admin-finance:'];
  } else if (/^\/v1\/admin\/courier-directory(?:\/|$)/.test(path)) {
    prefixes = ["admin-courier-directory:", "provider-shipping-guidance:"];
  } else if (path.startsWith("/v1/admin/")) {
    prefixes = ["admin-"];
  } else return;
  invalidateDashboardResources(key => prefixes.some(prefix => key.startsWith(prefix)));
}

export async function tinkrApiFetch<T>(
  session: Session,
  path: string,
  init: RequestInit = {},
  policy: { preserveIdempotencyKeyOnReauth?: boolean } = {}
): Promise<T> {
  const request = async (
    requestSession: Session,
    options: { replaceIdempotencyKey?: boolean } = {}
  ): Promise<T> => {
    const headers = new Headers(init.headers);
    headers.set("x-tinkr-supabase-access-token", currentApiSession(requestSession).access_token);

    if (options.replaceIdempotencyKey && headers.has("idempotency-key")) {
      headers.set("idempotency-key", idempotencyKey("reauth-retry"));
    }

    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }

    const result = await requestJson<T>(path, { ...init, headers }, apiSessionSignal(requestSession));
    if (!["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase())) invalidateAfterWrite(path);
    return result;
  };

  try {
    return await request(session);
  } catch (caught) {
    if (
      caught instanceof TinkrApiError &&
      caught.code === "reauth_required" &&
      reauthHandler
    ) {
      const purpose = reauthPurposeFromDetails(caught.details);
      if (purpose) {
        let nextSession: Session | null | void;
        try {
          nextSession = await reauthHandler({
            error: caught,
            purpose,
            session
          });
        } catch {
          throw caught;
        }
        if (nextSession) {
          currentApiSession(session); // reject a reauth that completed after sign-out/account switch
          if (nextSession.user.id !== session.user.id) throw caught;
          setCurrentApiSession(nextSession);
        }
        return request(nextSession ?? session, {
          // Transaction-owned finance routes do not cache a failed auth
          // response. Preserve the caller's key so an uncertain prior success
          // can still recover its existing result after fresh authentication.
          replaceIdempotencyKey: !policy.preserveIdempotencyKeyOnReauth
        });
      }
    }
    throw caught;
  }
}

export async function tinkrApiPublicFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers = new Headers(init.headers);

  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  return requestJson<T>(path, { ...init, headers });
}
